import NumberFlow from '@number-flow/react';
import moment from 'moment';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { Delta, Ellipsis, Empty, Paragraph, Sparkline, Table, tokens } from '@treams/treams-ui';

import { FAILED_SCORE, type TreamsScoreRow, isFailedScore } from '../TreamsScore.types';
import { toDisplayPreviousScore, toDisplayScore } from '../scoreDisplay';
import { TreamsScoreManagers } from './TreamsScoreManagers';

type SortDirection = 'asc' | 'desc';

function formatComparisonDate(date: Date, language?: string) {
	const value = moment(date);
	if (language) value.locale(language);
	return value.format('D MMMM YYYY');
}

const COLUMN_WIDTHS = {
	name: 200,
	score: 120,
	development: 150,
	trend: 110,
	manager: 240,
} as const;

type ComparisonTableProps = {
	teams: TreamsScoreRow[];
	managerAvatars?: Map<string, string>;
	trends?: Map<string, number[]>;
	nameColumnHeader: string;
	comparisonDate?: Date;
	showManagerColumn?: boolean;
	emptyState?: {
		icon?: ReactNode;
		title?: string;
		description?: string;
	};
	embedded?: boolean;
};

export function TreamsScoreComparisonTable({
	teams,
	managerAvatars,
	trends,
	nameColumnHeader,
	comparisonDate,
	showManagerColumn = true,
	emptyState,
	embedded,
}: ComparisonTableProps) {
	const { t, i18n } = useTranslation();

	const comparisonLabel = comparisonDate
		? t('analytics.treamsScore.card.comparedTo', {
				date: formatComparisonDate(comparisonDate, i18n.language),
			})
		: undefined;

	const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

	const sortedTeams = useMemo(
		() =>
			[...teams].sort((a, b) => {
				const diff = a.scores.total.score - b.scores.total.score;
				return sortDirection === 'desc' ? -diff : diff;
			}),
		[teams, sortDirection],
	);

	const toggleSort = () => setSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));

	return (
		<Table embedded={embedded}>
			<Table.Header>
				<Table.Cell grow minWidth={COLUMN_WIDTHS.name}>
					{nameColumnHeader}
				</Table.Cell>
				<Table.Cell width={COLUMN_WIDTHS.score} sorted={sortDirection} onClick={toggleSort}>
					{t('analytics.treamsScore.column.total')}
				</Table.Cell>
				<Table.Cell width={COLUMN_WIDTHS.development}>
					{t('analytics.treamsScore.column.development')}
				</Table.Cell>
				<Table.Cell width={COLUMN_WIDTHS.trend}>
					{t('analytics.treamsScore.column.trend')}
				</Table.Cell>
				<Table.Cell width={COLUMN_WIDTHS.manager}>
					{showManagerColumn ? t('analytics.treamsScore.column.manager') : null}
				</Table.Cell>
			</Table.Header>

			{sortedTeams.length === 0 ? (
				<Table.Row tall>
					<Table.Cell grow align="center">
						<Empty
							icon={emptyState?.icon}
							title={emptyState?.title ?? t('analytics.treamsScore.empty.title')}
							description={emptyState?.description ?? t('analytics.treamsScore.empty.description')}
						/>
					</Table.Cell>
				</Table.Row>
			) : (
				sortedTeams.map((team) => (
					<ComparisonRow
						key={team.id}
						team={team}
						managerAvatars={managerAvatars}
						showManagerColumn={showManagerColumn}
						trend={trends?.get(team.id)}
						comparisonLabel={comparisonLabel}
						t={t}
					/>
				))
			)}
		</Table>
	);
}

function ComparisonRow({
	team,
	managerAvatars,
	showManagerColumn,
	trend,
	comparisonLabel,
	t,
}: {
	team: TreamsScoreRow;
	managerAvatars?: Map<string, string>;
	showManagerColumn: boolean;
	trend?: number[];
	comparisonLabel?: string;
	t: ReturnType<typeof useTranslation>['t'];
}) {
	const total = team.scores.total;
	const failed = isFailedScore(total.score);
	const displayTotal = toDisplayScore(total.score);

	return (
		<Table.Row tall>
			<Table.Cell grow minWidth={COLUMN_WIDTHS.name}>
				<Box minWidth={0} width="100%">
					<Ellipsis
						minWidth={0}
						width="100%"
						title={team.name}
						style={{ color: tokens.text.primary }}
					>
						{team.name}
					</Ellipsis>
					<Paragraph small style={{ color: tokens.text.secondary }}>
						{t('analytics.treamsScore.table.people', { count: team.people })}
					</Paragraph>
				</Box>
			</Table.Cell>

			<Table.Cell width={COLUMN_WIDTHS.score}>
				{failed ? (
					<Box style={{ color: tokens.text.secondary, fontSize: 14 }}>{FAILED_SCORE}</Box>
				) : (
					<Box style={{ fontSize: 15, color: tokens.text.primary }}>
						<NumberFlow
							value={displayTotal}
							format={{ maximumFractionDigits: 0 }}
							locales="en-US"
						/>
					</Box>
				)}
			</Table.Cell>

			<Table.Cell width={COLUMN_WIDTHS.development}>
				{failed || team.comparisonAvailable === false ? (
					<Box style={{ color: tokens.text.secondary }}>-</Box>
				) : (
					<Delta
						currentValue={displayTotal}
						previousValue={toDisplayPreviousScore(total.score, total.delta)}
						unit="numeric"
						tooltip={comparisonLabel}
						tooltipPortal
					/>
				)}
			</Table.Cell>

			<Table.Cell width={COLUMN_WIDTHS.trend}>
				{failed || !trend || trend.length < 2 ? null : (
					<Sparkline values={trend} width={64} height={24} />
				)}
			</Table.Cell>

			<Table.Cell width={COLUMN_WIDTHS.manager}>
				{showManagerColumn &&
					(team.managers.length === 0 ? (
						<Box style={{ color: tokens.text.secondary, fontSize: 14 }}>-</Box>
					) : (
						<TreamsScoreManagers managers={team.managers} avatarByID={managerAvatars} />
					))}
			</Table.Cell>
		</Table.Row>
	);
}
